import React, { useEffect, useState } from "react";
import { Loader2, Truck, MapPin, Phone, Check, X, RotateCcw, Route, Printer, Navigation } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";

/* Delivery , the local run. Tick the drops, build the route, tick them off.
   Mobile-first: everything stacks into cards. */

const EDGE = "#E8DFCB";
const AUB = "#3E1F46";

// Map queries carry address fields only , never customer names or phone numbers.
const sanitizeAddress = (a) => String(a || "").replace(/\S+@\S+/g, "").replace(/(\+?\d[\d\s-]{6,}\d)/g, "").replace(/\s{2,}/g, " ").replace(/^[\s,·-]+|[\s,·-]+$/g, "").trim();
const mapsUrlFor = (stops) => {
  const addrs = stops.map((s) => sanitizeAddress(s.address)).filter(Boolean).slice(0, 10).map(encodeURIComponent);
  if (addrs.length === 0) return null;
  if (addrs.length === 1) return `https://www.google.com/maps/search/?api=1&query=${addrs[0]}`;
  return `https://www.google.com/maps/dir/${addrs.join("/")}`;
};

const printRunSheet = (stops) => {
  const rows = stops.map((s) => `
    <tr>
      <td class="n">${s.stop_number}</td>
      <td><strong>${s.customer_name}</strong><br/><span class="sm">#${s.order_number} · ${s.region} · NZ$${s.total.toFixed(2)} ${s.payment_status}</span></td>
      <td>${s.address || "No address on file"}${s.phone ? `<br/><span class="sm">${s.phone}</span>` : ""}${s.delivery_notes ? `<br/><em class="sm">${s.delivery_notes}</em>` : ""}</td>
      <td class="tick"></td>
    </tr>`).join("");
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`<html><head><title>Delivery run sheet</title><style>
    body{font-family:Georgia,serif;color:#3C2A1E;padding:22px;}
    h1{font-size:26px;margin:0;color:#3E1F46;}
    .sub{font-size:11px;letter-spacing:.3em;text-transform:uppercase;color:#6E1E3A;}
    table{width:100%;border-collapse:collapse;margin-top:16px;}
    th,td{border:1px solid #C9B89A;padding:8px 10px;text-align:left;vertical-align:top;font-size:13px;}
    th{background:#F3EBDD;font-size:10px;letter-spacing:.2em;text-transform:uppercase;}
    .n{width:34px;text-align:center;font-weight:bold;font-size:16px;}
    .sm{font-size:11px;color:#7A6A55;}
    .tick{width:60px;}
  </style></head><body>
    <div class="sub">✦ Heritage Dahlias · Local run</div>
    <h1>Delivery run sheet</h1>
    <div class="sm">${new Date().toLocaleString("en-NZ")} · ${stops.length} stops</div>
    <table><thead><tr><th>Stop</th><th>Customer</th><th>Address &amp; notes</th><th>Done</th></tr></thead>
    <tbody>${rows}</tbody></table>
  </body></html>`);
  w.document.close();
  w.focus();
  w.print();
};

export default function AdminDelivery() {
  const [data, setData] = useState(null);
  const [region, setRegion] = useState("All");
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = async (reg = region) => {
    try {
      const r = await api.get("/admin/delivery", { params: { region: reg } });
      setData(r.data);
    } catch { toast.error("Couldn't load the delivery queue."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(region); /* eslint-disable-next-line */ }, [region]);

  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const buildRoute = async () => {
    if (selected.size === 0) return toast.error("Tick the orders going on this run first.");
    setBusy("route");
    try {
      const r = await api.post("/admin/delivery/route", { order_ids: [...selected] });
      toast.success(`Route built , ${r.data.stops.length} stops.`);
      setSelected(new Set());
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't build that route."); }
    finally { setBusy(""); }
  };

  const setStatus = async (order, status) => {
    setBusy(order.id);
    try {
      await api.post(`/admin/delivery/${order.id}/status`, { status });
      toast.success(status === "delivered" ? `${order.order_number} delivered.`
        : status === "failed" ? `${order.order_number} marked couldn't deliver.`
        : `${order.order_number} back in the queue.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update that stop."); }
    finally { setBusy(""); }
  };

  if (loading) return <div className="text-[#7A6A55] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading deliveries…</div>;
  if (!data) return null;

  const route = data.orders.filter((o) => o.stop_number);
  const queue = data.orders.filter((o) => !o.stop_number);

  return (
    <div className="space-y-5 max-w-4xl" data-testid="admin-delivery">
      <header>
        <div className="text-[10px] tracking-[0.42em] uppercase font-semibold text-[#6E1E3A]">✦ Local run</div>
        <h1 className="font-serif text-4xl sm:text-5xl mt-1" style={{ color: AUB }}>Delivery</h1>
        <p className="text-sm italic text-[#7A6A55] mt-1">
          Orders you're dropping off yourself. Tick the ones going out, build the route, then tick them off as you go.
        </p>
      </header>

      <div className="flex flex-wrap gap-1.5" data-testid="delivery-filters">
        {data.regions.map((r) => (
          <button key={r} type="button" onClick={() => { setRegion(r); setSelected(new Set()); }}
            data-testid={`delivery-filter-${r.replace(/\s+/g, "-").toLowerCase()}`}
            className="px-3 h-9 border rounded-full text-[11px] font-bold uppercase tracking-[0.12em]"
            style={region === r ? { backgroundColor: AUB, borderColor: AUB, color: "#fff" } : { borderColor: EDGE, color: "#3C2A1E" }}>
            {r} ({data.counts?.[r] ?? 0})
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={buildRoute} disabled={selected.size === 0 || busy === "route"} data-testid="delivery-build-route"
          className="inline-flex items-center gap-2 px-5 h-12 bg-[#6E1E3A] text-white text-[11px] font-bold uppercase tracking-[0.14em] disabled:opacity-40">
          {busy === "route" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Route className="w-4 h-4" />}
          Build delivery route{selected.size ? ` (${selected.size})` : ""}
        </button>
        {selected.size === 0 && queue.length > 0 && (
          <span className="self-center text-xs italic text-[#7A6A55]" data-testid="delivery-route-hint">
            Tick the drops below to build today's run.
          </span>
        )}
        {route.length > 0 && (
          <>
            <a href={mapsUrlFor(route)} target="_blank" rel="noreferrer noopener" data-testid="delivery-navigate-run"
              className="inline-flex items-center gap-2 px-4 h-12 bg-[#1A73E8] hover:bg-[#0F5DC4] text-white text-[11px] font-bold uppercase tracking-[0.14em]">
              <MapPin className="w-4 h-4" /> Navigate the run
            </a>
            <button type="button" onClick={() => printRunSheet(route)} data-testid="delivery-print-run"
              className="inline-flex items-center gap-2 px-4 h-12 border text-[11px] font-bold uppercase tracking-[0.14em] text-[#3C2A1E]" style={{ borderColor: EDGE }}>
              <Printer className="w-4 h-4" /> Print run sheet
            </button>
            <button type="button" onClick={() => api.post("/admin/delivery/clear-route").then(() => load())} data-testid="delivery-clear-route"
              className="inline-flex items-center gap-2 px-4 h-12 border text-[11px] font-bold uppercase tracking-[0.14em] text-[#3C2A1E]" style={{ borderColor: EDGE }}>
              <RotateCcw className="w-4 h-4" /> Clear route
            </button>
          </>
        )}
      </div>

      {route.length > 0 && (
        <section className="space-y-2" data-testid="delivery-route">
          <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">Today's run , {route.length} stops</div>
          {route.map((o) => (
            <article key={o.id} className="bg-white border rounded p-4" style={{ borderColor: o.delivery_status === "failed" ? "#A33030" : EDGE }}
              data-testid={`delivery-stop-${o.stop_number}`}>
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="px-2 py-0.5 rounded-full bg-[#3E1F46] text-white text-[10px] font-bold tracking-[0.14em] uppercase">Stop {o.stop_number}</span>
                <strong className="font-serif text-[17px] text-[#3C2A1E]">{o.customer_name}</strong>
                <span className="text-xs text-[#9C8B73]">#{o.order_number}</span>
                <span className="text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded bg-[#F3EBDD] text-[#6E1E3A] font-semibold">{o.region}</span>
                {o.delivery_status === "failed" && <span className="text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded bg-[#A33030] text-white font-semibold">Couldn't deliver</span>}
                <span className="ml-auto text-xs text-[#7A6A55]">NZ${o.total.toFixed(2)} · {o.payment_status}</span>
              </div>
              <StopDetails o={o} />
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={() => setStatus(o, "delivered")} disabled={busy === o.id} data-testid={`delivery-delivered-${o.id}`}
                  className="inline-flex items-center gap-1.5 px-4 h-11 bg-[#1F5B3F] text-white text-[10px] font-bold uppercase tracking-[0.14em] disabled:opacity-40">
                  <Check className="w-3.5 h-3.5" /> Delivered
                </button>
                <button type="button" onClick={() => setStatus(o, "failed")} disabled={busy === o.id} data-testid={`delivery-failed-${o.id}`}
                  className="inline-flex items-center gap-1.5 px-4 h-11 border border-[#A33030] text-[#A33030] text-[10px] font-bold uppercase tracking-[0.14em] disabled:opacity-40">
                  <X className="w-3.5 h-3.5" /> Couldn't deliver
                </button>
                <button type="button" onClick={() => setStatus(o, "queued")} disabled={busy === o.id} data-testid={`delivery-requeue-${o.id}`}
                  className="inline-flex items-center gap-1.5 px-4 h-11 border text-[10px] font-bold uppercase tracking-[0.14em] text-[#3C2A1E] disabled:opacity-40" style={{ borderColor: EDGE }}>
                  <RotateCcw className="w-3.5 h-3.5" /> Return to queue
                </button>
                {o.address && (
                  <a href={mapsUrlFor([o])} target="_blank" rel="noreferrer noopener" data-testid={`delivery-navigate-${o.id}`}
                    className="inline-flex items-center gap-1.5 px-4 h-11 border border-[#1A73E8] text-[#1A73E8] text-[10px] font-bold uppercase tracking-[0.14em]">
                    <Navigation className="w-3.5 h-3.5" /> Navigate
                  </a>
                )}
              </div>
            </article>
          ))}
        </section>
      )}

      <section className="space-y-2">
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">
          Delivery queue{region !== "All" ? ` , ${region}` : ""} ({queue.length})
        </div>
        {queue.length === 0 ? (
          <div className="bg-white border border-dashed rounded p-8 text-center text-sm text-[#7A6A55]" style={{ borderColor: EDGE }} data-testid="delivery-empty">
            Nothing waiting for delivery. Send packed orders here from the Packing tab.
          </div>
        ) : queue.map((o) => (
          <label key={o.id} className="bg-white border rounded p-4 flex gap-3 cursor-pointer" style={{ borderColor: selected.has(o.id) ? AUB : EDGE }}
            data-testid={`delivery-order-${o.id}`}>
            <input type="checkbox" checked={selected.has(o.id)} onChange={() => toggle(o.id)} data-testid={`delivery-select-${o.id}`}
              className="w-5 h-5 mt-0.5 accent-[#3E1F46]" />
            <div className="flex-1">
              <div className="flex flex-wrap items-baseline gap-2">
                <strong className="font-serif text-[17px] text-[#3C2A1E]">{o.customer_name}</strong>
                <span className="text-xs text-[#9C8B73]">#{o.order_number}</span>
                <span className="text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded bg-[#F3EBDD] text-[#6E1E3A] font-semibold">{o.region}</span>
                <span className="ml-auto text-xs text-[#7A6A55]">NZ${o.total.toFixed(2)} · {o.payment_status}</span>
              </div>
              <StopDetails o={o} />
            </div>
          </label>
        ))}
      </section>
    </div>
  );
}

const StopDetails = ({ o }) => (
  <div className="mt-1.5 text-sm text-[#3C2A1E] space-y-0.5">
    <div className="flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 mt-0.5 text-[#9C8B73] shrink-0" />{o.address || "No address on file"}</div>
    {o.phone && <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#9C8B73]" /><a href={`tel:${o.phone}`} className="hover:underline">{o.phone}</a></div>}
    {o.delivery_notes && <div className="flex items-start gap-1.5 italic text-[#6E1E3A]"><Truck className="w-3.5 h-3.5 mt-0.5 shrink-0" />{o.delivery_notes}</div>}
  </div>
);
